import moment from 'moment';

import { feClearOldAppointment } from 'actions/appointment.actions';
import beCreateAppointment from 'actions/appointment/beCreateAppointment.action';
import beUpdateAppointment from 'actions/appointment/beUpdateAppointment.action';
import feBookingCleanUpNewClient from 'actions/booking/feBookingCleanUpNewClient.action';
import { SET_START_TIME } from 'actions/booking/feSetBookingStartTime.types';
import { CHARGE_NOSHOW_FAILURE, CHARGE_NOSHOW_SUCCESS } from 'actions/payment/beChargeCardOnFile.action';
import { CHANGE_PLACEHOLDER_POSITION } from 'actions/scheduler/feChangeSchedulerPlaceholder.types';
import fePickCurrentTime from 'actions/scheduler/fePickCurrentTime.action';
import { aTypeRebook, aTypeRebookPreload } from 'actions/shared.types';
import { SAVE_LOCAL_CLIENT_TAGS } from 'actions/tags/saveLocalTags.types';
import feUpdateTimeOffData from 'actions/timeoff/feUpdateTimeOffData.action';
import { feCleanCheckoutData, feRebookDirtyTime } from 'actions/ui.actions';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import getString from 'utils/accessors/getString.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToIso } from 'utils/converters/time/from.util';
import { nowAsIso } from 'utils/date/now.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import {
  ADD_SERVICE_BOOKING,
  BOOKING_NEW_CLIENT_FAILURE,
  BOOKING_NEW_CLIENT_REQUEST,
  BOOKING_PICK_CLIENT,
  BOOKING_UPDATE_CLIENT_FAILURE,
  BOOKING_UPDATE_CLIENT_LIST_SUCCESS,
  BOOKING_UPDATE_CLIENT_REQUEST,
  CHANGE_APPOINTMENT_STATUS_FAILURE,
  CHANGE_APPOINTMENT_STATUS_REQUEST,
  CHANGE_APPOINTMENT_STATUS_SUCCESS,
  CHANGE_BOOKING_NOTES,
  CHANGE_REPEAT_FREQ,
  CHANGE_REPEAT_FREQTYPE,
  CHANGE_REPEAT_STATUS,
  CHANGE_REPEAT_UNTIL,
  CLEAN_NEWBOOKING_DATA,
  CLEAR_CHARGE_ERROR,
  CLEAR_DELETE_APPOINTMENT_INFO,
  CLEAR_FUTURE_BOOKING_DATA,
  CLIENT_LAST_VISIT_FAILURE,
  CLIENT_LAST_VISIT_REQUEST,
  CLIENT_LAST_VISIT_SUCCESS,
  CLIENT_NO_SHOW_RATE_FAILURE,
  CLIENT_NO_SHOW_RATE_REQUEST,
  CLIENT_NO_SHOW_RATE_SUCCESS,
  CREATE_APPOINTMENT_WITH_SERVICES_FAILURE,
  CREATE_APPOINTMENT_WITH_SERVICES_REQUEST,
  CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
  CREATE_DEPOSIT_BACK_END_FAILURE,
  CREATE_DEPOSIT_BACK_END_REQUEST,
  CREATE_DEPOSIT_BACK_END_SUCCESS,
  CREATE_DEPOSIT_IN_CHECKOUT,
  CS_TEAM_FAILURE,
  CS_TEAM_REQUEST,
  CS_TEAM_SUCCESS,
  DELETE_APPOINTMENT_FAILURE,
  DELETE_APPOINTMENT_REQUEST,
  DELETE_APPOINTMENT_SUCCESS,
  EDIT_SERVICE,
  EDIT_SERVICE_CLIENT,
  EDIT_SERVICE_DURATION,
  EDIT_SERVICE_PRICE,
  EDIT_SERVICE_PROCESSING,
  EMPTY_APPOINTMENT_STATUS,
  FETCH_APPOINTMENT_FOR_EDIT_FAILURE,
  FETCH_APPOINTMENT_FOR_EDIT_REQUEST,
  FETCH_APPOINTMENT_FOR_EDIT_SUCCESS,
  FETCH_APPOINTMENT_STARTTIME_FAILURE,
  FETCH_APPOINTMENT_STARTTIME_REQUEST,
  FETCH_APPOINTMENT_STARTTIME_SUCCESS,
  FETCH_CLIENT_FAILURE,
  FETCH_CLIENT_REQUEST,
  FETCH_CLIENT_SUCCESS,
  FETCH_EVENT_FOR_EDIT_FAILURE,
  FETCH_EVENT_FOR_EDIT_REQUEST,
  FETCH_EVENT_FOR_EDIT_SUCCESS,
  FREQUENTLY_LIST_FAILURE,
  FREQUENTLY_LIST_REQUEST,
  FREQUENTLY_LIST_SUCCESS,
  GET_CLIENT_DEPOSIT_DETAILS_FAILURE,
  GET_CLIENT_DEPOSIT_DETAILS_REQUEST,
  GET_CLIENT_DEPOSIT_DETAILS_SUCCESS,
  GET_PREFERENCE_SUCCESS,
  PICK_CATEGORY,
  REMOVE_ADDED_SERVICE,
  REMOVE_NEW_APPOINTMENT,
  SET_ALL_DAY,
  SET_CURRENT_STYLIST,
  SET_END_TIME,
  SET_IS_EDIT_BOOKING,
  SET_REPEAT_FOREVER,
  SET_REPEAT_RULE,
  SET_TYPE,
  SHOW_MORE_REPEAT_FAILURE,
  SHOW_MORE_REPEAT_REQUEST,
  SHOW_MORE_REPEAT_SUCCESS,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_FAILURE,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_REQUEST,
  UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_SUCCESS,
  UPDATE_APPOINTMENT_WITH_SERVICES_FAILURE,
  UPDATE_APPOINTMENT_WITH_SERVICES_REQUEST,
  UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS,
  UPDATE_CLIENT_DATA,
  UPDATE_NEWBOOKING_DATE,
  UPDATE_SERVICE_BOOKING,
} from 'booking/constants';
import { overridesForStylist } from 'booking/overrides';
import { CHECKOUT_CLOSE_SESSION_REQUEST, CHECKOUT_CLOSE_SESSION_SUCCESS, TRANSACTION_CREATE_SUCCESS } from 'checkout/constants';
import compareStartTimes from 'lib/sortTimesAscending.util';
import { REBOOK_TIME } from 'scheduler/components/common/RebookAppointmentDialog.component';
import { CLEAR_NOSHOW_CHARGE_INFO } from 'scheduler/constants';

const L = logger('bookingReducer');

const initState = () => ({
  frequency: '1',
  frequencyType: 'never_repeat',
  until: moment().set('y', moment().get('y') + 1),
  isRepeatBooking: false,
  isEditBooking: false,
  repeat_rule: 'once',
  allDay: false,
  forever: true,
  endDate: null,
  type: '',
  pickedCategory: {},
  endTime: undefined,
  startTime: undefined,
  currentStylistId: null,
  currentTime: null,
  lastVisited: {},
  isFetching: false,
  startedFetch: false,
  errorMessage: '',
  data: null,
  client: {
    clientTags: [],
  },
  preferenceList: [],
  saved: false,
  deposit: null,
  notes: '',
  addedServices: [],
  clientList: [],
  frequentServices: [],
  editClient: {
    isFetching: false,
    errorMessage: '',
    id: 0,
  },
  appointmentStatuses: {
    isFetching: false,
    errorMessage: '',
    appointments: [],
  },
  newClient: {
    isFetching: false,
    errorMessage: '',
    response: null,
  },
  fetchAppointment: {
    isFetching: false,
    errorMessage: '',
    response: null,
  },
  updateAppointment: {
    isFetching: false,
    errorMessage: '',
    response: null,
  },
  clientDepositData: {
    errorMessage: '',
    isFetching: false,
    response: null,
  },
  fetchEvent: {
    isFetching: false,
    errorMessage: '',
    response: null,
  },
  clientNoShowData: {
    isFetching: false,
    errorMessage: '',
    appointments: null,
    no_shows: null,
    id: null,
  },
  lastShownRepeatId: [],
  futureBookings: [],
  deletedAppointmentId: 0,
  depositData: {},
  depositAction: '',
  noshowChargeErrorInBooking: false,
  newAppointment: [],
  appointmentStartTimeList: [],
  isReBooking: false,
  newRebookCreated: false,
  oldAppointment: null,
  from: '',
  isCSteamSupport: false,
});

const bookingReducer = (state = initState(), action) => {
  const type = action?.type;
  const data = action?.data;

  if (!type) return state;

  if (feRebookDirtyTime.AT.name === type && data?.dirty) {
    const { unedited, ...newState } = state ?? {};
    L.debug('()', 'feRebookDirtyTime:', newState, '<-', action, '+', state);
    return newState;
  }

  const { endDate: stateEndDate, forever: stateForever, startTime: stateStartTime } = state ?? {};
  const {
    booking,
    bookingType,
    category: actionCategory,
    categoryServices,
    client,
    clientTagList,
    currentTime,
    custom,
    date,
    deposit: actionDeposit,
    duration,
    endDate,
    endTime,
    error,
    errorMessage,
    forever,
    frequency,
    frequencyType,
    from: actionFrom,
    id: actionId,
    isEditBooking,
    json,
    local,
    notes,
    oldAppointment: actionOldAppointment,
    params,
    price,
    processing,
    repeat_rule: repeatRule,
    response,
    service: actionService,
    services,
    startTime,
    stylistId: currentStylistId,
    stylists,
    until,
  } = action ?? {};
  const { procedures: responseProcedures, start_time: responseStartTime } = response ?? {};

  switch (type) {
    case CHANGE_REPEAT_UNTIL:
      return { ...state, until };
    case CHANGE_REPEAT_FREQ:
      return { ...state, frequency };
    case CHANGE_REPEAT_FREQTYPE:
      return { ...state, frequencyType };
    case CHANGE_REPEAT_STATUS:
      return { ...state, isRepeatBooking: !state.isRepeatBooking, until: moment(stateStartTime).add(1, 'years') };
    case SET_REPEAT_RULE:
      return { ...state, repeat_rule: repeatRule, endDate: stateForever ? null : stateEndDate };
    case SET_ALL_DAY:
      return { ...state, allDay: !state.allDay };
    case SET_IS_EDIT_BOOKING:
      return { ...state, isEditBooking };
    case SET_REPEAT_FOREVER:
      return { ...state, forever, endDate };
    case SET_TYPE:
      return { ...state, type: bookingType, endTime: moment(stateStartTime).add(1, 'hours') };
    case SET_START_TIME:
      return { ...state, startTime };
    case SET_END_TIME:
      return { ...state, endTime };
    case PICK_CATEGORY:
      return { ...state, pickedCategory: actionCategory };
    case GET_CLIENT_DEPOSIT_DETAILS_REQUEST:
      return { ...state, clientDepositData: { response: null, errorMessage: '', isFetching: true } };
    case GET_CLIENT_DEPOSIT_DETAILS_SUCCESS:
      return { ...state, clientDepositData: { response, errorMessage: '', isFetching: false } };
    case GET_CLIENT_DEPOSIT_DETAILS_FAILURE:
      return { ...state, clientDepositData: { response: [], errorMessage, isFetching: false } };
    case SAVE_LOCAL_CLIENT_TAGS:
      return { ...state, client: { ...state.client, client_tags: [...clientTagList] } };
    case CHANGE_BOOKING_NOTES:
      return { ...state, notes };

    case aTypeRebookPreload.name:
      return { ...state, ...booking, currentStylistId: booking.stylist.id };
    case fePickCurrentTime.AT.name:
      return { ...state, startTime: asObject(data).time };
    case feUpdateTimeOffData.AT.name: {
      const block = fromAnyDateToIso(getAnyOrNull('block.from.value', data));
      const full = fromAnyDateToIso(asObject(data).fullDay);
      const start = fromAnyDateToIso(state.startTime);
      const now = nowAsIso();

      const result = full || block || start || now;
      L.debug('()', feUpdateTimeOffData.AT.name, result, '<-', { full, block, start, now });

      // NOTE: old code might depend on this being a Moment instance, so convert it
      return { ...state, startTime: moment(result) };
    }

    case SET_CURRENT_STYLIST:
      return { ...state, currentStylistId, currentTime, saved: false };
    case CHANGE_PLACEHOLDER_POSITION:
      return { ...state, currentStylistId, startTime, saved: false };
    case CLIENT_LAST_VISIT_REQUEST:
      return { ...state, lastVisited: {} };
    case CLIENT_LAST_VISIT_FAILURE:
      return error.code === 404 ? { ...state, lastVisited: {} } : state;
    case CLIENT_LAST_VISIT_SUCCESS:
      return {
        ...state,
        lastVisited: { date: responseStartTime, stylist: responseProcedures[0].stylist.user_name },
      };
    case REMOVE_ADDED_SERVICE: {
      const toDeleted = local
        ? (service) => (service.local_id === actionId ? { ...service, deleted: true } : service)
        : (service) => (service.id === actionId ? { ...service, deleted: true } : service);

      return { ...state, addedServices: state.addedServices.map(toDeleted) };
    }
    case EDIT_SERVICE_CLIENT:
      return {
        ...state,
        addedServices: state.addedServices.map((service) => ({
          ...service,
          appointment: {
            ...service.appointment,
            client,
          },
        })),
      };
    case EDIT_SERVICE:
      return {
        ...state,
        addedServices: state.addedServices.map((service) => {
          if ((service.local_id && service.local_id === actionId) || (service.id && service.id === actionId)) {
            return {
              ...service,
              duration: actionService.duration,
              processing: actionService.processing,
              price: Number(actionService.price),
            };
          }
          return service;
        }),
      };
    case EDIT_SERVICE_DURATION: {
      return {
        ...state,
        addedServices: state.addedServices.map((service) => {
          if ((service.local_id && service.local_id === actionId) || (service.id && service.id === actionId)) {
            return { ...service, duration, is_custom_duration: !!custom };
          }
          return service;
        }),
      };
    }
    case EDIT_SERVICE_PROCESSING:
      return {
        ...state,
        addedServices: state.addedServices.map((service) => {
          if ((service.local_id && service.local_id === actionId) || (service.id && service.id === actionId)) {
            return { ...service, processing, is_custom_processing: !!custom };
          }
          return service;
        }),
      };
    case EDIT_SERVICE_PRICE:
      return {
        ...state,
        addedServices: state.addedServices.map((service) => {
          const id = parseInt(actionId, 10);
          const localId = service.local_id;

          return localId && localId === id && service.id && service.id === id
            ? {
                ...service,
                price: Number(price),
                is_custom_price: !!custom,
              }
            : service;
        }),
      };
    case BOOKING_NEW_CLIENT_REQUEST:
      return { ...state, newClient: { isFetching: true, startedFetch: true, response: null, errorMessage: '' } };
    case [feBookingCleanUpNewClient.name]:
      return { ...state, newClient: { isFetching: false, startedFetch: false, response: null, errorMessage: '' } };
    case BOOKING_UPDATE_CLIENT_LIST_SUCCESS:
      return { ...state, newClient: { isFetching: false, startedFetch: true, response, errorMessage: '' } };
    case BOOKING_NEW_CLIENT_FAILURE:
      return { ...state, newClient: { isFetching: false, startedFetch: true, response: null, errorMessage } };
    case FETCH_CLIENT_REQUEST:
      return { ...state, editClient: { ...state.edit_client, isFetching: true, response: null, errorMessage: '' } };
    case FETCH_CLIENT_SUCCESS:
      return { ...state, editClient: { ...state.edit_client, isFetching: false, response }, client: response };
    case FETCH_CLIENT_FAILURE:
      return { ...state, editClient: { ...state.edit_client, isFetching: false, errorMessage } };
    case BOOKING_UPDATE_CLIENT_REQUEST:
      return { ...state, editClient: { ...state.edit_client, isFetching: true } };
    case BOOKING_UPDATE_CLIENT_FAILURE:
      return { ...state, editClient: { ...state.edit_client, isFetching: false, errorMessage } };
    case UPDATE_CLIENT_DATA:
      return { ...state, editClient: { ...state.edit_client, isFetching: false, errorMessage: '' }, client: response };
    case beCreateAppointment.AT.init:
      return { ...state, isFetching: true, errorMessage: '' };

    case beCreateAppointment.AT.pass: {
      const { client_id: clientId } = json;
      const { deposit } = params;
      const start = getString('json.procedures[0].start_time', action);
      const isFutureAppointment = moment(start).isAfter(moment());
      const { newAppointment, from, isReBooking } = state ?? {};
      const { procedures } = json;

      let totalPrice = 0;

      if (procedures && procedures.length > 0) {
        for (let i = 0; i < procedures.length; i += 1) {
          totalPrice += procedures[i].price;
        }
      }

      if (totalPrice > 0 && isFutureAppointment && clientId && !fromAnyToBool(deposit)) {
        newAppointment[0] = json;
      }

      let updatedAppointment = newAppointment.slice();

      if (from === REBOOK_TIME.before) {
        updatedAppointment = [];
      }

      return {
        ...state,
        isFetching: false,
        newAppointment: updatedAppointment,
        newRebookCreated: isReBooking ? { from } : null,
        isReBooking: false,
        isPosDeposit: true,
      };
    }

    case beCreateAppointment.AT.fail:
      return { ...state, isFetching: false, errorMessage };

    case CREATE_APPOINTMENT_WITH_SERVICES_REQUEST:
      return { ...state, isFetching: true, errorMessage: '', addedServices: [] };

    case CREATE_APPOINTMENT_WITH_SERVICES_SUCCESS: {
      const { client_id: clientId } = json;
      const { deposit } = params;
      const start = getString('json.procedures[0].start_time', action);
      const isFutureAppointment = moment(start).isAfter(moment());
      const { newAppointment, from, isReBooking } = state ?? {};
      const phone = getAnyOrNull('json.client.phone', action);
      const { procedures } = json;
      let totalPrice = 0;
      if (procedures && procedures.length > 0) {
        for (let i = 0; i < procedures.length; i += 1) {
          totalPrice += procedures[i].price;
        }
      }
      if (totalPrice > 0 && phone && isFutureAppointment && clientId && !fromAnyToBool(deposit)) {
        newAppointment[0] = json;
      }
      let updatedAppointment = newAppointment.slice();
      if (from === REBOOK_TIME.before) {
        updatedAppointment = [];
      }

      return {
        ...state,
        isFetching: false,
        addedServices: [],
        saved: true,
        newAppointment: updatedAppointment,
        newRebookCreated: isReBooking ? { from } : null,
        isReBooking: false,
      };
    }

    case REMOVE_NEW_APPOINTMENT: {
      const { newAppointment } = state;
      newAppointment.splice(0, 1);
      return { ...state, newAppointment };
    }
    case CREATE_APPOINTMENT_WITH_SERVICES_FAILURE:
      return { ...state, isFetching: false, errorMessage };
    case FETCH_APPOINTMENT_FOR_EDIT_REQUEST:
      return { ...state, saved: false, fetchAppointment: { isFetching: true, errorMessage: null, response: null } };
    case FETCH_APPOINTMENT_FOR_EDIT_SUCCESS: {
      const addedServices = [];
      responseProcedures.sort(compareStartTimes);
      for (let i = 0; i < responseProcedures.length; i += 1) {
        const newService = responseProcedures[i];
        newService.editing = true;
        newService.start_time = moment(newService.start_time);
        newService.end_time = moment(newService.end_time);
        newService.stylist_id = newService.stylist.id;
        newService.service_id = newService.service.id;
        newService.category = newService.service.category.id;
        newService.name = newService.service.name;
        newService.local_id = newService.id;
        addedServices.push(newService);
      }
      return {
        ...state,
        addedServices,
        client: response.client,
        notes: response.notes,
        fetchAppointment: { isFetching: false, response, errorMessage: null },
      };
    }
    case FETCH_APPOINTMENT_FOR_EDIT_FAILURE:
      return { ...state, fetchAppointment: { isFetching: false, response: null, errorMessage } };
    case UPDATE_APPOINTMENT_WITH_SERVICES_REQUEST:
      return { ...state, updateAppointment: { isFetching: true, errorMessage: '', response: null } };

    case beUpdateAppointment.AT.pass:
      return { ...state, errorMessage: '', updateAppointment: { isFetching: false, errorMessage: '', response } };

    case beUpdateAppointment.AT.fail:
      return { ...state, updateAppointment: { isFetching: false, errorMessage, response: null } };

    case UPDATE_APPOINTMENT_WITH_SERVICES_SUCCESS:
      return {
        ...state,
        saved: true,
        errorMessage: '',
        addedServices: [],
        updateAppointment: { isFetching: false, errorMessage: '', response },
      };
    case UPDATE_APPOINTMENT_WITH_SERVICES_FAILURE:
      return { ...state, updateAppointment: { isFetching: false, errorMessage, response: null } };
    case UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_REQUEST:
      return { ...state, updateAppointment: { isFetching: true, errorMessage: '', response: null } };
    case UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_SUCCESS:
      return {
        ...state,
        saved: true,
        errorMessage: '',
        addedServices: [],
        updateAppointment: { isFetching: false, errorMessage: '', response },
      };
    case UPDATE_APPOINTMENT_WITH_REPEATING_SERVICES_FAILURE:
      return { ...state, updateAppointment: { isFetching: false, errorMessage, response: null } };
    case FETCH_EVENT_FOR_EDIT_REQUEST:
      return { ...state, saved: false, fetchEvent: { isFetching: true, errorMessage: null, response: null } };
    case FETCH_EVENT_FOR_EDIT_SUCCESS:
      return { ...state, fetchEvent: { isFetching: false, response, errorMessage: null } };
    case FETCH_EVENT_FOR_EDIT_FAILURE:
      return { ...state, fetchAppointment: { isFetching: false, response: null, errorMessage } };
    case ADD_SERVICE_BOOKING: {
      const placeholder = {
        start_time: stateStartTime || null,
        duration: '00:15:00',
        processing: '00:00:00',
        stylist: { id: state.currentStylistId },
      };
      const servicesCondition = state.addedServices && state.addedServices.length > 0;
      const lastService =
        servicesCondition && state.addedServices.filter((s) => s.deleted !== true).length > 0 ? state.addedServices.filter((s) => s.deleted !== true).slice(-1)[0] : state.addedServices.slice(-1)[0];
      const firstService = servicesCondition && state.addedServices.filter((s) => s.deleted !== true).length > 0 ? state.addedServices.filter((s) => s.deleted !== true)[0] : null;
      const deletedService = servicesCondition && state.addedServices.filter((s) => s.deleted === true).length > 0 ? state.addedServices.filter((s) => s.deleted === true)[0] : null;
      let start = placeholder.start_time;
      let placeholderStylistId = placeholder.stylist.id;
      if (servicesCondition) {
        if (deletedService && !firstService) {
          start = moment(deletedService.start_time);
          placeholderStylistId = deletedService.stylist_id;
        } else if (
          deletedService &&
          moment(lastService.start_time).toISOString() !== moment(deletedService.start_time).toISOString() &&
          moment(firstService.start_time).toISOString() !== moment(deletedService.start_time).toISOString()
        ) {
          start = moment(deletedService.start_time);
          placeholderStylistId = deletedService.stylist_id;
        } else {
          start = moment(lastService.start_time).add(moment.duration(lastService.duration)).add(moment.duration(lastService.processing));
          if (deletedService) {
            placeholderStylistId = deletedService.stylist_id;
          } else if (firstService) {
            placeholderStylistId = firstService.stylist_id;
          }
        }
      } else if (actionService.start_time) {
        start = actionService.start_time;
      }
      const composeService = {
        ...actionService,
        stylist_id: placeholderStylistId,
        start_time: start,
        end_time: servicesCondition ? moment(start).add(moment.duration(actionService.duration)) : moment(start).add(moment.duration(placeholder.duration)),
        appointment: placeholder.appointment ? placeholder.appointment : actionService.appointment,
      };

      const {
        price: overridePrice,
        duration: overrideDuration,
        processing: overrideProcessing,
      } = overridesForStylist({
        categoryServices,
        service: composeService,
        stylistId: composeService.stylist_id,
        stylists,
      });

      let { service } = composeService;
      for (let i = 0; i < categoryServices.length; i += 1) {
        const category = categoryServices[i];
        for (let j = 0; j < category.services.length; j += 1) {
          if (category.services[j].id === composeService.service_id) {
            service = category.services[j];
            service = { ...service, category: { ...category, services: [] } };
            break;
          }
        }
        if (service) {
          break;
        }
      }
      let nextId = -1000;
      const lastAddedService = state.addedServices.slice(-1)[0];
      if (lastAddedService) {
        if (lastAddedService.local_id < 0) {
          nextId = lastAddedService.local_id - 1;
        }
      }

      const newService = {
        ...composeService,
        id: nextId,
        price: overridePrice,
        duration: overrideDuration,
        processing: overrideProcessing,
        service,
        editing: true,
        local_id: nextId,
      };
      const newAddedServices = [...state.addedServices, newService];
      return { ...state, addedServices: newAddedServices };
    }
    case UPDATE_SERVICE_BOOKING: {
      if (!actionService.service_id) {
        return { ...state };
      }

      const {
        price: overridePrice,
        duration: overrideDuration,
        processing: overrideProcessing,
      } = overridesForStylist({
        categoryServices,
        service: actionService,
        stylistId: actionService.stylist_id,
        stylists,
      });

      const oldStylistId = actionService.stylist ? actionService.stylist.id : null;
      const stylistChanged = actionService.stylist_id !== oldStylistId;
      const updatedService = {
        ...actionService,
        price: overridePrice,
        stylist_id: stylistChanged ? actionService.stylist_id : actionService.stylist.id,
        duration: stylistChanged ? overrideDuration : actionService.duration,
        processing: stylistChanged ? overrideProcessing : actionService.processing,
        stylist: stylistChanged ? stylists.find((stylist) => stylist.id === actionService.stylist_id) : actionService.stylist,
      };

      const updatedServices = state.addedServices.filter((s) => s.deleted !== true);

      let found = false;
      let bookOrder;
      if (updatedServices.length > 0) {
        for (let i = 0; i < updatedServices.length; i += 1) {
          if ((actionService.local_id && updatedServices[i].local_id === actionService.local_id) || (actionService.id && actionService.id === updatedServices[i].id)) {
            updatedServices[i] = updatedService;
            found = true;
            bookOrder = i;
            break;
          }
        }
        if (!found) {
          updatedServices.push(updatedService);
        }
      }

      // this is the first service
      if (updatedServices.length > 1 && found && bookOrder === 0) {
        updatedService.end_time = moment(updatedService.start_time).add(moment.duration(updatedService.duration)).add(moment.duration(updatedService.processing));

        for (let i = 0; i < updatedServices.length; i += 1) {
          const currentUpdatedService = updatedServices[i];
          const nextUpdatedService = updatedServices[i + 1];

          if (
            nextUpdatedService &&
            (moment(nextUpdatedService.start_time).get('hour') < 5 || moment(nextUpdatedService.start_time).toISOString().indexOf('00:000Z') < 0 || nextUpdatedService.editing)
          ) {
            nextUpdatedService.start_time = moment(currentUpdatedService.end_time);
            nextUpdatedService.end_time = moment(nextUpdatedService.start_time).add(moment.duration(nextUpdatedService.duration)).add(moment.duration(nextUpdatedService.processing));
          }

          if (nextUpdatedService && nextUpdatedService.deleted !== true && !nextUpdatedService.stylist_id) {
            const currentStylistId = currentUpdatedService.stylist_id;

            const { price: overridePriceForServiceUpdate } = overridesForStylist({
              categoryServices,
              service: nextUpdatedService,
              stylistId: currentStylistId,
              stylists,
            });

            nextUpdatedService.stylist_id = currentStylistId;
            nextUpdatedService.price = overridePriceForServiceUpdate;
          }
        }
      }

      return { ...state, addedServices: [...updatedServices] };
    }
    case BOOKING_PICK_CLIENT:
      return {
        ...state,
        client,
        addedServices: state.addedServices.map((item) => ({ ...item, appointment: { ...item.appointment, client } })),
      };
    case CLEAN_NEWBOOKING_DATA: {
      const { oldAppointment, newRebookCreated, isReBooking, from } = state ?? {};
      return {
        ...initState(),
        oldAppointment,
        newRebookCreated, // newRebookCreated needed to take user to the checkout
        isReBooking,
        from,
      };
    }
    case feCleanCheckoutData.AT.name:
      return {
        ...state,
        newRebookCreated: null, // newRebookCreated used to go to checkout, now clear it
        isReBooking: false,
        oldAppointment: null,
      };

    case CHECKOUT_CLOSE_SESSION_REQUEST:
    case CHECKOUT_CLOSE_SESSION_SUCCESS:
    case TRANSACTION_CREATE_SUCCESS:
      return { ...state, oldAppointment: null, newRebookCreated: null, isReBooking: false };

    case UPDATE_NEWBOOKING_DATE: {
      let newData = [];
      let newDayServices = [];

      if (date) {
        if (state.addedServices && state.addedServices.length > 0) {
          newDayServices = state.addedServices.slice().map((service) => ({
            ...service,
            start_time: moment(service.start_time)
              .set({
                year: date.year(),
                month: date.month(),
                date: date.date(),
                hours: moment(service.start_time).get('hour'),
                minutes: moment(service.start_time).get('minutes'),
                second: 0,
                millisecond: 0,
              })
              .toISOString(),
            end_time: moment(service.end_time).set({
              year: date.year(),
              month: date.month(),
              date: date.date(),
              hours: moment(service.end_time).get('hour'),
              minutes: moment(service.end_time).get('minutes'),
              second: 0,
              millisecond: 0,
            }),
          }));
        }

        if (state.data && state.data.slice) {
          newData = state.data.slice();
          newData.map(
            (service) =>
              service.start_time.set({
                year: date.year(),
                month: date.month(),
                date: date.date(),
                hours: moment(service.start_time).get('hour'),
                minutes: moment(service.start_time).get('minutes'),
                second: 0,
                millisecond: 0,
              }) &&
              service.end_time.set({
                year: date.year(),
                month: date.month(),
                date: date.date(),
                hours: moment(service.end_time).get('hour'),
                minutes: moment(service.end_time).get('minutes'),
                second: 0,
                millisecond: 0,
              }),
          );
        }
      }
      return { ...state, data: newData, addedServices: newDayServices };
    }
    case DELETE_APPOINTMENT_REQUEST:
      return {
        ...state,
        deleteAppointmentIsFetching: true,
        deleteAppointmentId: params.appointment,
        deleteAppointmentErrorMessage: '',
      };
    case DELETE_APPOINTMENT_SUCCESS:
      // when deleted, need the deposit_amount and deposit_source for success popup
      return {
        ...state,
        deleteAppointmentIsFetching: false,
        deleteAppointmentErrorMessage: '',
        deletedAppointmentId: params.appointment,
        deposit_amount: params.deposit_amount,
        deposit_source: params.deposit_source,
        depositAction: params.depositAction,
      };
    case CLEAR_DELETE_APPOINTMENT_INFO:
      return { ...state, deletedAppointmentId: 0, deposit_source: '', deposit_amount: 0, depositAction: '' };
    case DELETE_APPOINTMENT_FAILURE:
      return { ...state, deleteAppointmentIsFetching: false, deleteAppointmentErrorMessage: errorMessage };

    case CHANGE_APPOINTMENT_STATUS_REQUEST:
      return { ...state, appointmentStatuses: { isFetching: true, errorMessage: '', response: null } };
    case CHANGE_APPOINTMENT_STATUS_SUCCESS: {
      // see also scheduler reducer
      let deleteAppointmentIsFetching;
      let deleteAppointmentErrorMessage;
      let deletedAppointmentId;
      let depositAmount;
      let depositSource;
      let depositAction;
      if (response[0].status === 'deleted') {
        deleteAppointmentIsFetching = false;
        deleteAppointmentErrorMessage = '';
        deletedAppointmentId = response[0].id;
        depositAmount = response[0].amount;
        depositSource = response[0].deposit_source;
        depositAction = response[0].action;
      }

      return {
        ...state,
        appointmentStatuses: {
          isFetching: false,
          errorMessage: '',
          response,
          deposit_amount: response.deposit_amount,
          deposit_source: response.deposit_source,
        },
        deleteAppointmentIsFetching,
        deleteAppointmentErrorMessage,
        deletedAppointmentId,
        deposit_amount: depositAmount,
        deposit_source: depositSource,
        depositAction,
      };
      // appointmentDetails,
    }
    case CLEAR_NOSHOW_CHARGE_INFO:
      // after cancel an appointment, these info with trigger a model
      // success charged and show rebook or send sms
      // if click rebook or send sms we need to clear the deposit info to get rid of modal
      return { ...state, deletedAppointmentId: 0, deposit_source: '', deposit_amount: 0, depositAction: '' };
    case CHANGE_APPOINTMENT_STATUS_FAILURE:
      return { ...state, appointmentStatuses: { isFetching: false, errorMessage, response: null } };

    case aTypeRebook.name: {
      for (const addedService of asArray(state.addedServices)) {
        for (const existingService of asArray(services)) {
          if (addedService.service_id !== existingService.service.id) continue;
          addedService.price = existingService.price;
          addedService.duration = existingService.duration;
          addedService.service.price = existingService.price;
          addedService.service.duration = existingService.duration;
          addedService.color = existingService.service.category.color;
          addedService.stylist_id = existingService.stylist.id;
        }
      }

      // appointmentStatuses used by Scheduler to show popup, after charge no-show and rebook, clear it
      return {
        ...state,
        client,
        addedServices: state.addedServices,
        isReBooking: true,
        oldAppointment: actionOldAppointment,
        from: actionFrom,
        appointmentStatuses: { response: null },
      };
    }

    case feClearOldAppointment.AT.name:
      // newRebookCreated: null, need this to decide whether to go to checkout
      return { ...state, isReBooking: false, oldAppointment: null, from: null };

    case CHARGE_NOSHOW_SUCCESS:
      return {
        ...state,
        oldAppointment: null,
        newRebookCreated: null, // newRebookCreated is used to show 'go to checkout', when cancelled, don't need checkout
        isReBooking: false,
      };
    case CREATE_DEPOSIT_IN_CHECKOUT:
      return { ...state, deposit: actionDeposit };
    case CREATE_DEPOSIT_BACK_END_REQUEST:
      return { ...state };
    case CREATE_DEPOSIT_BACK_END_SUCCESS:
      return { ...state, isPosDeposit: false };
    case CREATE_DEPOSIT_BACK_END_FAILURE:
      return { ...state, isPosDeposit: false };
    case CLIENT_NO_SHOW_RATE_REQUEST:
      return { ...state, clientNoShowData: { loading: true, errorMessage: null } };
    case CLIENT_NO_SHOW_RATE_SUCCESS: {
      return {
        ...state,
        clientNoShowData: {
          loading: false,
          errorMessage: null,
          appointments: response.appointments,
          no_shows: response.no_shows,
          id: response.id,
        },
      };
    }

    case CLIENT_NO_SHOW_RATE_FAILURE:
      return { ...state, clientNoShowData: { loading: false, errorMessage } };
    case FREQUENTLY_LIST_REQUEST:
      return { ...state, frequentServices: { isFetching: true, errorMessage: '', response: null } };

    case FREQUENTLY_LIST_SUCCESS:
      return { ...state, frequentServices: response };

    case FREQUENTLY_LIST_FAILURE:
      return { ...state, frequentServices: { isFetching: false, errorMessage, response: null } };
    case GET_PREFERENCE_SUCCESS:
      return { ...state, preferenceList: response };
    case SHOW_MORE_REPEAT_REQUEST:
      return { ...state, futureBookings: [], isFetching: true };
    case SHOW_MORE_REPEAT_SUCCESS: {
      const newFutureBookings = response;
      const lastBooking = newFutureBookings[newFutureBookings.length - 1];
      const { lastShownRepeatId } = state;
      const keys = lastShownRepeatId.map((k) => Object.keys(k)[0]);
      const index = keys.indexOf(`k${lastBooking.parent_id}`);

      if (index >= 0) {
        lastShownRepeatId[index][`k${lastBooking.parent_id}`] = lastBooking.id;
      } else {
        lastShownRepeatId.push({ [`k${lastBooking.parent_id}`]: lastBooking.id });
      }
      let { futureBookings } = state;

      if (futureBookings.length > 0 && futureBookings.length <= lastShownRepeatId.length * 5) {
        // if this is a different repeat booking
        if (futureBookings[futureBookings.length - 1].parent_id !== lastBooking.parent_id) {
          futureBookings.push(...newFutureBookings);
        }
      } else {
        futureBookings = newFutureBookings;
      }
      const compare = (a, b) => {
        if (a.procedures[0].start_time < b.procedures[0].start_time) {
          return -1;
        }
        return 1;
      };
      futureBookings.sort(compare);
      return { ...state, futureBookings, lastShownRepeatId, isFetching: false };
    }
    case SHOW_MORE_REPEAT_FAILURE:
      return { ...state, isFetching: false, errorMessage };
    case CLEAR_FUTURE_BOOKING_DATA:
      return { ...state, futureBookings: [] };
    case EMPTY_APPOINTMENT_STATUS:
      return { ...state, appointmentStatuses: { errorMessage: '', response: null } };
    case CHARGE_NOSHOW_FAILURE:
      return { ...state, noshowChargeErrorInBooking: true };
    case CLEAR_CHARGE_ERROR:
      return { ...state, noshowChargeErrorInBooking: false };
    case FETCH_APPOINTMENT_STARTTIME_REQUEST:
      return { ...state, appointmentStartTimeList: [], isFetching: true };
    case FETCH_APPOINTMENT_STARTTIME_SUCCESS:
      return { ...state, appointmentStartTimeList: response, isFetching: false };
    case FETCH_APPOINTMENT_STARTTIME_FAILURE:
      return { ...state, isFetching: false };
    // cs team
    case CS_TEAM_REQUEST:
      return { ...state, isFetching: true };
    case CS_TEAM_SUCCESS:
      return { ...state, isCSteamSupport: response, isFetching: false };
    case CS_TEAM_FAILURE:
      return { ...state, isFetching: false };
    default:
      return state;
  }
};

export default bookingReducer;
